import { handleFilesWithEditor } from "@/components/mailEditor/fileHandlers";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import CustomLink from "@/lib/tiptapLink";
import { mergeAttributes, Node } from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import { Color } from "@tiptap/extension-color";
import FileHandler from "@tiptap/extension-file-handler";
import FontFamily from "@tiptap/extension-font-family";
import { Heading } from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

const CustomHorizontalRule = Node.create({
    name: "horizontalRule",
    group: "block",
    parseHTML() {
        return [{ tag: "hr" }];
    },
    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: (element) => (element as HTMLHRElement).id || null,
            },
        };
    },
    renderHTML({ HTMLAttributes }) {
        return ["hr", mergeAttributes(HTMLAttributes)];
    },
});

const createEditorExtensions = (
    setAttachments?: Dispatch<SetStateAction<EmailAttachment[]>>,
    placeholder?: string,
) => {
    const { t } = useTranslation();

    return [
        StarterKit.configure({
            paragraph: false,
            bold: false,
            heading: false,
            blockquote: false,
            horizontalRule: false,
        }),
        Paragraph.extend({
            addAttributes() {
                return {
                    style: {
                        parseHTML: (element) => element.style?.cssText,
                    },
                    id: {
                        parseHTML: (element) =>
                            (element as HTMLParagraphElement).id,
                    },
                };
            },
            renderHTML({ HTMLAttributes }) {
                // On force la font-family dans le style, en la fusionnant avec le style existant
                const style = HTMLAttributes.style || "";
                const fontFamily =
                    "font-family: 'Source Sans 3', Arial, sans-serif";
                const mergedStyle = style.includes("font-family")
                    ? style
                    : (style ? `${style}; ` : "") + fontFamily;

                return ["p", { ...HTMLAttributes, style: mergedStyle }, 0];
            },
        }),
        Bold.extend({
            addAttributes() {
                return {
                    style: {
                        parseHTML: (element) => element.style?.cssText,
                    },
                };
            },
            renderHTML(props) {
                return ["strong", props.HTMLAttributes, 0];
            },
        }),
        CustomLink.extend({
            addAttributes() {
                return {
                    href: {
                        default: null,
                    },
                    target: {
                        default: "_blank",
                    },
                    rel: {
                        default: "noopener noreferrer",
                    },
                    style: {
                        parseHTML: (element: HTMLElement) =>
                            element.style?.cssText,
                    },
                };
            },
            renderHTML(props) {
                const { href, target, rel, ...otherAttrs } =
                    props.HTMLAttributes;

                return [
                    "a",
                    {
                        href,
                        target,
                        rel,
                        class: "cursor-pointer text-blue-600 underline hover:text-blue-800",
                        title: href
                            ? t("richTextEditor.tooltipClickToOpen", {
                                  url: href,
                              })
                            : undefined,
                        ...otherAttrs,
                    },
                    0,
                ];
            },
            addCommands() {
                return {
                    setLink:
                        (attributes: { href: string }) =>
                        ({ commands }) =>
                            commands.setMark("link", attributes),
                    toggleLink:
                        (attributes: { href: string }) =>
                        ({ commands }) =>
                            commands.toggleMark("link", attributes),
                    unsetLink:
                        () =>
                        ({ commands }) =>
                            commands.unsetMark("link"),
                };
            },
        }),
        Image.extend({
            addAttributes() {
                return {
                    style: {
                        default: null,
                        parseHTML: (element) => element.style?.cssText,
                    },
                    src: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("src"),
                    },
                    width: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("width"),
                    },
                    height: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("height"),
                    },
                    alt: {
                        default: null,
                        parseHTML: (element) => element.getAttribute("alt"),
                    },
                };
            },
            renderHTML(props) {
                // Filter out null/undefined attributes to prevent width="null" or height="null"
                const { width, height, ...restAttrs } = props.HTMLAttributes;
                const attrs: Record<string, unknown> = { ...restAttrs };

                if (width != null && width !== "") {
                    attrs.width = width;
                }
                if (height != null && height !== "") {
                    attrs.height = height;
                }

                return ["img", attrs];
            },
        }).configure({ allowBase64: true }),
        Underline,
        TextStyle,
        Color,
        FontFamily,
        Highlight.configure({
            multicolor: true,
        }),
        Heading.configure({
            levels: [1, 2, 3],
        }),
        Blockquote,
        TextAlign.configure({
            types: ["heading", "paragraph"],
        }),
        Placeholder.configure({
            placeholder: placeholder || "",
            emptyEditorClass: "is-editor-empty",
        }),
        Table.extend({
            addAttributes() {
                return {
                    style: {
                        parseHTML: (element) => element.style?.cssText,
                    },
                    id: {
                        parseHTML: (element) =>
                            (element as HTMLTableElement).id,
                    },
                };
            },
            renderHTML(props) {
                return ["table", props.HTMLAttributes, 0];
            },
        }).configure({
            allowTableNodeSelection: true,
        }),
        TableRow.extend({
            addAttributes() {
                return {
                    style: {
                        parseHTML: (element) => element.style?.cssText,
                    },
                };
            },
            renderHTML(props) {
                return ["tr", props.HTMLAttributes, 0];
            },
        }),
        TableCell.extend({
            addAttributes() {
                return {
                    style: {
                        parseHTML: (element) => element.style?.cssText,
                    },
                    width: {
                        parseHTML: (element) =>
                            (element as HTMLTableCellElement).width,
                    },
                };
            },
            renderHTML(props) {
                return ["td", props.HTMLAttributes, 0];
            },
        }),
        TableHeader.extend({
            addAttributes() {
                return {
                    style: {
                        parseHTML: (element) => element.style?.cssText,
                    },
                };
            },
            renderHTML(props) {
                return ["th", props.HTMLAttributes, 0];
            },
        }),
        FileHandler.configure({
            // File handler does work only when editor is focused
            onDrop: (currentEditor, files, pos) => {
                handleFilesWithEditor(
                    files,
                    currentEditor,
                    pos,
                    setAttachments,
                );
            },
            onPaste: (currentEditor, files, htmlContent) => {
                if (htmlContent) {
                    // if there is htmlContent, stop manual insertion & let other extensions handle insertion via inputRule
                    // you could extract the pasted file from this url string and upload it to a server for example
                    console.log(htmlContent); // eslint-disable-line no-console

                    return false;
                }

                handleFilesWithEditor(
                    files,
                    currentEditor,
                    currentEditor.state.selection.anchor,
                    setAttachments,
                );
            },
        }),
        CustomHorizontalRule,
    ];
};

export default createEditorExtensions;
